import { initPopovers, disposePopover } from '../core/bootstrap-helpers.js';
import { spinnerHtml, escapeHtml } from '../core/utilities.js';
import { validateOrderRows, orderRowsIn } from './validation.js';
import { rowPartIdentity } from './row-identity.js';
import {
    brandSettingsFromResponse,
    buildInventoryBatchEntry,
    computeInventoryTotals,
    defaultBrandSettings,
    deriveStockDisplay,
} from '../core/stock-display.js';

/**
 * Ignore stale inventory responses when the user changes line/inputs quickly.
 * The token is stored per element rather than in one module-wide counter: with a
 * single counter, refreshing N rows only ever painted the last response and left
 * the other N-1 rows stuck on their spinner. Both the per-row and the batched
 * lookup claim through here, so neither can paint over the other.
 */
var inventoryRequestSeq = 0;

function claimInventoryRequest($element) {
    inventoryRequestSeq++;
    $element.data('paceInventorySeq', inventoryRequestSeq);
    return inventoryRequestSeq;
}

function inventoryRequestIsCurrent($element, seq) {
    return $element.data('paceInventorySeq') === seq;
}

/**
 * Which item a row's rendered stock and price actually describe.
 *
 * The token above answers "is this response still wanted?"; this answers the question the
 * token cannot, because it outlives the request: "does what is on the row still describe
 * what the row says?" Nothing re-requests inventory when the buyer retypes a part number,
 * so without this a reordered row keeps the previous part's stock figure and account price,
 * keeps .valid, and stays submittable - the display naming one part while the order carries
 * another (#706).
 *
 * Stamped from the row's live inputs rather than from the outgoing request payload, so the
 * stamp and the later comparison are computed by the same code over the same source. The
 * payload is not that source: getItemInventory() percent-decodes partnumber for the
 * warehouse, and comparing a decoded stamp against a raw input would read as an edit on
 * every keystroke for any part number carrying an escape.
 *
 * In .data() for the reason row-identity.js gives: keyed on the element, never serialised
 * into the form, and not carried by the .clone() the modal's row-creation paths use, so a
 * blank row cloned off a painted one cannot inherit a claim about inventory it never had.
 */
var INVENTORY_IDENTITY_KEY = 'paceInventoryIdentity';

function stampRowInventoryIdentity($row) {
    $row.data(INVENTORY_IDENTITY_KEY, rowPartIdentity($row));
}

// defaultBrandSettings() and brandSettingsFromResponse() moved to core/stock-display.js
// with the rest of the shared derivation (#301). The null-echo guard lives in the
// shared brandSettingsFromResponse(): catalog/ajaxGetBrand echoes null for an unknown
// code, and a NULL LeadTimeDays column must not come back as a truthy '0' string.

/** Brand settings are configuration, not stock, so one lookup per page is enough. */
var brandSettingsCache = {};

/**
 * Resolve brand settings for `aaiaCodes` and hand a lookup map to `callback`.
 * One request per distinct brand: the batched inventory path used to skip this
 * entirely, so a backorderable row with only AAM/manufacturer stock rendered as
 * 0, got .error and blocked submit.
 *
 * Exported for the supersession alert (partials/superseded-inventory.js), which
 * needs the brand settings of a part that is not on the page any other way.
 */
export function loadBrandSettings(aaiaCodes, callback) {
    var resolved = {};
    var pending = [];
    var i;

    for (i = 0; i < aaiaCodes.length; i++) {
        var code = aaiaCodes[i];

        if (!code || resolved.hasOwnProperty(code)) {
            continue;
        }

        if (brandSettingsCache.hasOwnProperty(code)) {
            resolved[code] = brandSettingsCache[code];
        } else if (pending.indexOf(code) === -1) {
            pending.push(code);
        }
    }

    if (!pending.length) {
        callback(resolved);
        return;
    }

    var remaining = pending.length;
    var settle = function () {
        remaining--;

        if (remaining < 1) {
            callback(resolved);
        }
    };

    for (i = 0; i < pending.length; i++) {
        (function (code) {
            $.ajax({
                url: 'catalog/ajaxGetBrand/' + code,
                type: 'post',
                dataType: 'json',
                success: function (data) {
                    resolved[code] = brandSettingsFromResponse(data);
                    brandSettingsCache[code] = resolved[code];
                },
                // A brand lookup failure must not strand the inventory request:
                // fall back to the defaults this path used before, and do not
                // cache them so the next open retries.
                error: function () {
                    resolved[code] = defaultBrandSettings();
                },
                complete: settle,
            });
        })(pending[i]);
    }
}

/**
 * No ordering until a lookup has answered for every row. validateOrderRows() is
 * what turns these back on, and only once every row is .valid.
 */
function disableOrderSubmit($table) {
    var $form = $table.closest('form');

    $form.find('button[type="submit"]').prop('disabled', true);
    $form.find('.instant-order').prop('disabled', true);
}

/**
 * Spinner in the stock cell and no price: a leftover cost reads as current.
 *
 * Token-checked like every read-back path. The brand lookup runs ahead of the
 * inventory request, so this can fire long after the rows were claimed - without
 * the check it would wipe a row that a newer per-row lookup has since painted,
 * and the batch response would then decline to repaint it (token superseded),
 * stranding that row on a spinner until the user touched it again.
 */
function paintInventoryPending(rows, seqs) {
    for (var i = 0; i < rows.length; i++) {
        var $row = $(rows[i]);

        if (!inventoryRequestIsCurrent($row, seqs[i])) {
            continue;
        }

        $row
            .find('.multi-order-inventory')
            .html('<span class="text-muted">' + spinnerHtml() + '</span>');
        $row.find('.multi-order-cost').empty();
    }
}

/**
 * Strip the derived display off the given rows and put them back to "unverified". Typed
 * part number, brand and quantity are left alone - only what a lookup produced is dropped.
 */
function clearRowInventoryDisplay($rows) {
    // The stock badge owns a popover anchored to <body>; emptying the cell while
    // it is open would leave the tip on screen after the modal closes.
    $rows.find('[data-bs-toggle="popover"]').each(function () {
        disposePopover($(this));
    });

    $rows.find('.multi-order-inventory').empty();
    $rows.find('.multi-order-cost').empty();
    $rows.removeClass('valid error no-inst');

    $rows.each(function () {
        var $row = $(this);

        // Supersede anything still in flight so it cannot paint after the close.
        claimInventoryRequest($row);
        // Nothing is displayed now, so there is no claim left to go stale.
        $row.removeData(INVENTORY_IDENTITY_KEY);
    });
}

/**
 * Every real row at once. Reopening the quick order modal otherwise shows numbers fetched
 * during the previous session, and validateOrderRows() would re-enable submit against them.
 */
export function clearOrderRowInventory($table) {
    clearRowInventoryDisplay(orderRowsIn($table));

    disableOrderSubmit($table);
}

/**
 * Drop one row's rendered stock and price once it stops describing the part the row names,
 * and put the row back to unverified so validateOrderRows() withholds submit until a lookup
 * answers for what the buyer actually typed.
 *
 * Only rows a lookup was issued for carry a stamp, so a row nobody has looked up is left
 * alone - there is nothing on it to be stale, and clearing it would strip a .error a failed
 * lookup just set.
 *
 * Clearing rather than re-requesting is deliberate. The lookup is a POST per keystroke
 * otherwise, and the paths that legitimately re-identify a row already fire their own:
 * picking a suggestion goes through applyPartSelection(), and touching the brand goes
 * through the modal's .multi-order-brand handler. What is missing is not a request, it is
 * the admission that the numbers on screen no longer describe anything.
 */
export function dropStaleOrderRowInventory($row) {
    var described = $row.data(INVENTORY_IDENTITY_KEY);

    if (!described) {
        return false;
    }

    var current = rowPartIdentity($row);

    if (
        current.partNumber === described.partNumber &&
        current.aaia === described.aaia
    ) {
        return false;
    }

    clearRowInventoryDisplay($row);
    validateOrderRows($row.closest('table'));

    return true;
}

export var productAvailabilityAvailable = '';
export var productAvailabilityOutOfStock = '';
export var productAvailabilityUnavailable = '';
export var productAvailabilityBackorder = '';
export var productAvailabilityDropship = '';
/** #687: the two-figure form labels - stocked vs dropship/backorder-available. */
export var productAvailabilityInStock = '';
export var productAvailabilityDropshipAvailable = '';
export var pricingFallbackMessage = '';
export var disableOrderingOnFallback = false;
export var stockDisplayCap = 0;
/** Whether stock NUMBERS are shown at all (setting show_stock, via #show-stock). */
export var showStock = true;
/** Label for "order" from hidden `#order-text` (set via `setInventoryConfig`). */
export var customOrderText = '';

export function setInventoryConfig(config) {
    if (config.productAvailabilityAvailable !== undefined) {
        productAvailabilityAvailable = config.productAvailabilityAvailable;
    }
    if (config.productAvailabilityOutOfStock !== undefined) {
        productAvailabilityOutOfStock = config.productAvailabilityOutOfStock;
    }
    if (config.productAvailabilityUnavailable !== undefined) {
        productAvailabilityUnavailable = config.productAvailabilityUnavailable;
    }
    if (config.productAvailabilityBackorder !== undefined) {
        productAvailabilityBackorder = config.productAvailabilityBackorder;
    }
    if (config.productAvailabilityDropship !== undefined) {
        productAvailabilityDropship = config.productAvailabilityDropship;
    }
    if (config.productAvailabilityInStock !== undefined) {
        productAvailabilityInStock = config.productAvailabilityInStock;
    }
    if (config.productAvailabilityDropshipAvailable !== undefined) {
        productAvailabilityDropshipAvailable =
            config.productAvailabilityDropshipAvailable;
    }
    if (config.pricingFallbackMessage !== undefined) {
        pricingFallbackMessage = config.pricingFallbackMessage;
    }
    if (config.disableOrderingOnFallback !== undefined) {
        disableOrderingOnFallback = config.disableOrderingOnFallback;
    }
    if (config.stockDisplayCap !== undefined) {
        stockDisplayCap = config.stockDisplayCap;
    }
    if (config.showStock !== undefined) {
        showStock = config.showStock;
    }
    if (config.customOrderText !== undefined) {
        customOrderText =
            config.customOrderText != null
                ? String(config.customOrderText)
                : '';
    }
}

function popoverSpan(cssClass, content, inner) {
    return (
        '<span class="' +
        cssClass +
        '" data-bs-container="body" data-bs-toggle="popover" data-bs-placement="bottom" data-bs-trigger="hover" data-bs-content="' +
        escapeHtml(content) +
        '">' +
        inner +
        '</span>'
    );
}

/** The stock cell (a <td>) or the stock paragraph (an order-item modal), same rungs. */
function availabilityMarkup(asCell, cssClass, text, iconHtml) {
    if (asCell) {
        return iconHtml
            ? popoverSpan(cssClass, text, iconHtml)
            : '<span class="' + cssClass + ' small">' + escapeHtml(text) + '</span>';
    }

    return '<p class="' + cssClass + '">' + escapeHtml(text) + '</p>';
}

/** The site's own labels, as last set through setInventoryConfig(). */
function inventoryWordings() {
    return {
        available: productAvailabilityAvailable,
        outOfStock: productAvailabilityOutOfStock,
        unavailable: productAvailabilityUnavailable,
        backorder: productAvailabilityBackorder,
        dropship: productAvailabilityDropship,
        inStock: productAvailabilityInStock,
        dropshipAvailable: productAvailabilityDropshipAvailable,
    };
}

/**
 * Dress the shared derivation's view model (core/stock-display.js) in this surface's
 * markup. The rungs themselves - and the history of how they converged here - live on
 * deriveStockDisplay() now; what remains local is presentation:
 *
 *   - a count renders as a hover popover badge in a stock cell and as a plain
 *     paragraph in an order-item modal;
 *   - the check icon rides only statuses where stock exists somewhere (#686: an
 *     <i class="fa fa-check"> behind a hover popover is how the buyer ended up
 *     ordering a backordered part without being told - say the words instead);
 *   - when both the warehouse and the AAM/manufacturer pools hold stock (#687) the
 *     two figures render side by side, each under its own label.
 */
function availabilityViewHtml(view, asCell) {
    var check = view.inStock ? '<i class="fa fa-check"></i>' : '';

    switch (view.status) {
        case 'unavailable':
            return availabilityMarkup(
                asCell,
                'text-danger',
                view.message,
                '<i class="fa fa-exclamation-triangle"></i>'
            );

        case 'available':
            return availabilityMarkup(
                asCell,
                'text-success',
                view.message,
                check
            );

        case 'count':
            if (view.bothFigures) {
                if (asCell) {
                    return (
                        popoverSpan(
                            'badge bg-success',
                            view.stockedCount + ' ' + productAvailabilityInStock,
                            escapeHtml(view.stockedCount)
                        ) +
                        ' ' +
                        popoverSpan(
                            'badge bg-success',
                            view.additionalCount + ' ' + productAvailabilityDropshipAvailable,
                            escapeHtml(view.additionalCount)
                        )
                    );
                }

                return (
                    '<p class="text-success">' +
                    escapeHtml(
                        view.stockedCount +
                            ' ' +
                            productAvailabilityInStock +
                            ' \u00b7 ' +
                            view.additionalCount +
                            ' ' +
                            productAvailabilityDropshipAvailable
                    ) +
                    '</p>'
                );
            }

            if (asCell) {
                return popoverSpan(
                    'badge bg-success',
                    view.singleCount + ' in stock',
                    escapeHtml(view.singleCount)
                );
            }

            return (
                '<p class="text-success">' +
                escapeHtml(view.singleCount) +
                ' in stock</p>'
            );

        case 'dropship':
        case 'backorder':
            return availabilityMarkup(
                asCell,
                'text-success',
                view.message + view.leadTimeSuffix,
                check
            );

        default:
            return availabilityMarkup(
                asCell,
                'text-danger',
                view.message,
                ''
            );
    }
}

/**
 * Derive one row's availability from the response item and paint it, returning the
 * view model so the caller can keep its display/submittability pairing (#686): the
 * row ends up .valid exactly when the derivation did not say unavailable or
 * out-of-stock.
 */
function deriveRowAvailability(item, brandSettings, backordering, noStock) {
    return deriveStockDisplay({
        item: item,
        brandSettings: brandSettings,
        backordering: backordering,
        noStock: noStock,
        cap: stockDisplayCap,
        wordings: inventoryWordings(),
    });
}

/**
 * Whether stock numbers are hidden for the surface being painted.
 *
 * $element is either a row of a multi-order table (the class sits on that table, as
 * _common/_order_modal.inc renders it) or a whole order-item modal, #orderItemModal<key>
 * (the class sits on its .multi-order-inventory container). This used to be a hard-coded
 * $('#orderModal').find('.no-stock') no matter which of the two it was handed, and only
 * ever gave the right answer because layout.inc renders the quick order modal on the
 * same page from the same show_stock setting. #686
 */
function stockDisplayIsHidden($element) {
    var $table = $element.closest('table');

    if ($table.length) {
        return $table.hasClass('no-stock');
    }

    return $element.find('.no-stock').length > 0;
}

export function getItemInventory($element, productData) {
    var mySeq = claimInventoryRequest($element);

    stampRowInventoryIdentity($element);

    var viewAsAccount = $('#vaa').length ? '?au=' + $('#vaa').val() : '';
    var $inventoryMessage = $element.find('.multi-order-inventory');
    var $costMessage = $element.find('.multi-order-cost');
    var inventoryMessageIsCell = $inventoryMessage.is('td');
    var $quantity = $element.find('.multi-order-quantity');
    var quantity = $quantity.val();

    var aaia =
        typeof $element.attr('data-aaia') !== typeof undefined &&
        $element.attr('data-aaia') !== false
            ? $element.attr('data-aaia')
            : productData.aaia;

    var whlc = $element.attr('data-whlc');
    var whsn = $element.attr('data-whsn');

    var batchEntry = buildInventoryBatchEntry(
        Object.assign({}, productData, {
            aaia: aaia,
            linecode:
                productData.linecode == null || productData.linecode === ''
                    ? whlc
                    : productData.linecode,
            stocknumber:
                productData.stocknumber == null ||
                productData.stocknumber === ''
                    ? whsn
                    : productData.stocknumber,
        })
    );

    var backordering = $('#backordering').length
        ? $('#backordering').val()
        : false;

    var noStock = stockDisplayIsHidden($element);

    var brandSettings = defaultBrandSettings();

    $inventoryMessage.empty();
    $costMessage.empty();

    var ajaxOptions = {
        // No trailing slash: a redirect to the canonical URL drops POST body (no Payload in DevTools).
        url: 'whs/get_inventory_and_pricing' + viewAsAccount,
        type: 'post',
        dataType: 'json',
        data: {
            parts: [batchEntry],
        },
        error: function () {
            if (!inventoryRequestIsCurrent($element, mySeq)) {
                return;
            }
            if (inventoryMessageIsCell) {
                $inventoryMessage.html(
                    '<span class="text-danger small">Pricing unavailable</span>'
                );
            } else {
                $inventoryMessage.html(
                    '<p class="text-danger small">Pricing unavailable</p>'
                );
            }
            $costMessage.html('');
            if (inventoryMessageIsCell) {
                validateOrderRows($element.closest('table'));
            }
        },
        success: function (json) {
            if (!inventoryRequestIsCurrent($element, mySeq)) {
                return;
            }
            if (!json || !json[0]) {
                if (inventoryMessageIsCell) {
                    $inventoryMessage.html(
                        '<span class="text-warning small">Stock unavailable</span>'
                    );
                } else {
                    $inventoryMessage.html(
                        '<p class="text-warning small">Stock unavailable</p>'
                    );
                }
                if (inventoryMessageIsCell) {
                    validateOrderRows($element.closest('table'));
                }
                return;
            }

            var data = json[0];

            $quantity.attr('min', 0);

            var view = deriveRowAvailability(
                data,
                brandSettings,
                backordering,
                noStock
            );

            if (view.status === 'unavailable') {
                // productAvailabilityUnavailable, not a hard-coded English sentence:
                // every other availability string on this screen is the site's own
                // wording from the hidden #product-availability-* inputs. #686
                $inventoryMessage.html(availabilityViewHtml(view, inventoryMessageIsCell));

                $element.removeClass('valid').addClass('error');
            } else {
                $inventoryMessage.html(availabilityViewHtml(view, inventoryMessageIsCell));

                // Same predicate the derivation branched on, so the row this blocks
                // is exactly the row that reads "Out of Stock". #686
                if (view.status === 'out-of-stock') {
                    $quantity.val('');
                    $element.removeClass('valid').addClass('error');
                } else {
                    if (parseInt(quantity) > 0) {
                        $quantity.val(quantity);
                    } else {
                        $quantity.val('1');
                    }

                    $element.removeClass('error').addClass('valid');
                }
            }

            if ($costMessage.length && data.account_price) {
                var accountPrice = parseFloat(data.account_price);

                if (accountPrice > 0) {
                    $costMessage.text('$' + accountPrice.toFixed(2));
                }
            }

            if (inventoryMessageIsCell) {
                if (
                    data.disable_instant_order_on_no_inv &&
                    data.hasOwnProperty(
                        'warehouse_inventory_default_location'
                    ) &&
                    data.warehouse_inventory_default_location === 0
                ) {
                    $element.addClass('no-inst');
                }
                validateOrderRows($element.closest('table'));
            }

            initPopovers();
        },
    };

    $.ajax({
        url: 'catalog/ajaxGetBrand/' + aaia,
        type: 'post',
        dataType: 'json',
        beforeSend: function () {
            if (inventoryMessageIsCell) {
                $inventoryMessage.html(
                    '<span class="text-muted">' + spinnerHtml() + '</span>'
                );
            } else {
                $inventoryMessage.html(
                    '<span class="text-muted">' + spinnerHtml('Loading inventory...') + '</span>'
                );
            }
        },
        error: function () {
            if (!inventoryRequestIsCurrent($element, mySeq)) {
                return;
            }
            if (inventoryMessageIsCell) {
                $inventoryMessage.html(
                    '<span class="text-danger small">Brand lookup failed</span>'
                );
            } else {
                $inventoryMessage.html(
                    '<p class="text-danger small">Brand lookup failed</p>'
                );
            }
            if (inventoryMessageIsCell) {
                validateOrderRows($element.closest('table'));
            }
        },
        success: function (data) {
            if (!inventoryRequestIsCurrent($element, mySeq)) {
                return;
            }
            brandSettings = brandSettingsFromResponse(data);

            $.ajax(ajaxOptions);
        },
    });
}

export function getMultiItemInventory($table) {
    var backordering = $('#backordering').length
        ? $('#backordering').val()
        : false;
    var rows = [];
    var batches = [];
    var seqs = [];
    var aaiaCodes = [];
    var $auInput = $('#au');
    var viewAsAccount = $auInput.length ? '?au=' + $auInput.val() : '';

    orderRowsIn($table).each(function (i, row) {
        var $row = $(row);
        var partNumber = $row.find('.multi-order-part-number').val();
        var aaia = $row.find('.multi-order-brand').val();

        // Nothing to look up. Posting the row anyway comes back empty and paints a
        // bogus "0"/.error on it; the per-row path skips blank part numbers too.
        if (partNumber == null || !String(partNumber).trim().length) {
            return;
        }

        // A part number alone is not enough to identify an item: createPiesItems()
        // derives wh_linecode from the brand, so with neither the lookup returns
        // nothing and the row renders 0/.error. .btn-quick-order seeds exactly such
        // a row (search term, brand not chosen yet) - leave it blank until the user
        // picks a brand, which is when the per-row path takes over.
        var lineCode = $row.attr('data-whlc');
        if (
            (aaia == null || !String(aaia).trim().length) &&
            (lineCode == null || !String(lineCode).trim().length)
        ) {
            return;
        }

        rows.push(row);
        batches.push(
            buildInventoryBatchEntry({
                aaia: aaia,
                partnumber: partNumber,
                gtin: $row.find('.multi-order-gtin').val(),
                linecode: lineCode,
                stocknumber: $row.attr('data-whsn'),
            })
        );
        // Fall back to the line code, which ajaxGetBrand() answers null for: it resolves
        // an AAIA code only. This claimed a Model_brand::getByWHLineCode() fallback that
        // was never defined, so the call fataled rather than resolving anything (#603);
        // the null now yields the same all-zero defaults a missing code would. A reorder
        // line whose brand has no <option> on this site therefore still reads 0/.error
        // here while the per-row path reads "Place on Backorder"/.valid.
        aaiaCodes.push(aaia || lineCode);
        seqs.push(claimInventoryRequest($row));
        stampRowInventoryIdentity($row);
    });

    if (!batches.length) {
        // Every row was skipped, so nothing will paint and nothing will re-validate.
        // Leave ordering off rather than trusting whatever state the rows are in.
        disableOrderSubmit($table);
        return;
    }

    // Painted now as well as in beforeSend: the brand lookup below runs first, and
    // the previous session's numbers must not sit there while it does.
    paintInventoryPending(rows, seqs);
    disableOrderSubmit($table);

    var brandSettingsByAaia = {};

    var ajaxOptions = {
        url: 'whs/get_inventory_and_pricing' + viewAsAccount,
        type: 'post',
        dataType: 'json',
        data: { parts: batches },
        beforeSend: function () {
            paintInventoryPending(rows, seqs);
        },
        error: function () {
            $.each(rows, function (i, row) {
                var $row = $(row);

                if (!inventoryRequestIsCurrent($row, seqs[i])) {
                    return;
                }

                $row
                    .find('.multi-order-inventory')
                    .html(
                        '<span class="text-danger small">Pricing unavailable</span>'
                    );
                $row.find('.multi-order-cost').empty();
                // Unverified rows must not be submittable: validateOrderRows()
                // enables submit as soon as no row carries .error.
                $row.removeClass('valid').addClass('error');
            });

            validateOrderRows($table);
        },
        success: function (data) {
            var painted = {};

            for (var key in data) {
                var item = data[key];

                if (item == null || typeof item !== 'object') {
                    continue;
                }

                // Map by the response's own _index. The old rowIndex++ over
                // $table.find('tr') silently relied on .default-row being last.
                var rowIndex = item.hasOwnProperty('_index')
                    ? parseInt(item._index)
                    : parseInt(key);
                var $element = rows[rowIndex] ? $(rows[rowIndex]) : $();

                if (
                    !$element.length ||
                    !inventoryRequestIsCurrent($element, seqs[rowIndex])
                ) {
                    continue;
                }

                painted[rowIndex] = true;

                var $inventoryMessage = $element.find('.multi-order-inventory');
                var $costMessage = $element.find('.multi-order-cost');
                var noStock = stockDisplayIsHidden($element);
                var $quantity = $element.find('.multi-order-quantity');
                var quantity = $quantity.val();
                var brandSettings =
                    brandSettingsByAaia[aaiaCodes[rowIndex]] ||
                    defaultBrandSettings();

                if (!item.hasOwnProperty('warehouse_inventory_by_location')) {
                    $inventoryMessage.html(
                        '<span class="text-danger" data-bs-container="body" data-bs-toggle="popover" data-bs-placement="bottom" data-bs-trigger="hover" data-bs-content="An error occurred."><i class="fa fa-exclamation-triangle"></i></span>'
                    );
                    $element.removeClass('valid').addClass('error');
                } else {
                    $quantity.attr('min', 0);

                    var view = deriveRowAvailability(
                        item,
                        brandSettings,
                        backordering,
                        noStock
                    );

                    if (view.status === 'unavailable') {
                        $inventoryMessage.html(availabilityViewHtml(view, true));
                        $element.removeClass('valid').addClass('error');
                    } else {
                        $inventoryMessage.html(availabilityViewHtml(view, true));

                        // Same predicate the derivation branched on, so the row
                        // this blocks is exactly the row that reads "Out of
                        // Stock". #686
                        if (view.status === 'out-of-stock') {
                            $quantity.val('');
                            $element.removeClass('valid').addClass('error');
                        } else {
                            if (parseInt(quantity) > 0) {
                                $quantity.val(quantity);
                            } else {
                                $quantity.val('1');
                            }

                            $element.removeClass('error').addClass('valid');
                        }

                        if (
                            item.disable_instant_order_on_no_inv &&
                            item.hasOwnProperty(
                                'warehouse_inventory_default_location'
                            ) &&
                            item.warehouse_inventory_default_location === 0
                        ) {
                            $element.addClass('no-inst');
                        }
                    }

                    // This path never rendered the price at all, which is why the
                    // cost column came up blank for the cart and reorder flows.
                    // Inside the else: a row that failed to come back keeps the
                    // cleared cost rather than a price under an error triangle.
                    if ($costMessage.length && item.account_price) {
                        var accountPrice = parseFloat(item.account_price);

                        if (accountPrice > 0) {
                            $costMessage.text('$' + accountPrice.toFixed(2));
                        }
                    }
                }
            }

            // A row with no result of its own would keep its spinner forever, and
            // validateOrderRows() would still enable submit against it.
            for (var i = 0; i < rows.length; i++) {
                if (painted[i]) {
                    continue;
                }

                var $unanswered = $(rows[i]);

                if (!inventoryRequestIsCurrent($unanswered, seqs[i])) {
                    continue;
                }

                $unanswered
                    .find('.multi-order-inventory')
                    .html(
                        '<span class="text-warning small">Stock unavailable</span>'
                    );
                $unanswered.find('.multi-order-cost').empty();
                $unanswered.removeClass('valid').addClass('error');
            }

            validateOrderRows($table);

            initPopovers();
        },
    };

    // Brand settings first, then inventory - the same order getItemInventory()
    // uses, and the reason its display could disagree with this path's.
    loadBrandSettings(aaiaCodes, function (settings) {
        brandSettingsByAaia = settings;

        $.ajax(ajaxOptions);
    });
}
